import { useEffect, useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useFiguTour } from "@/components/figutour/FiguTourProvider";

const HALO_PADDING = 8;
const BACKDROP = "rgba(0, 0, 0, 0.72)";
const BLUR_CSS = "blur(2px)";

/**
 * FiguTour v2 overlay (Phase 16 rewrite).
 *
 * Four backdrop rects (TOP / LEFT / RIGHT / BOTTOM) placed AROUND the
 * target's bounding rect — the target itself sits in a natural, empty
 * cut-out region so its own pointer-events remain default `auto`.
 *
 * Container: `pointer-events: none` — its children own their own values.
 *   • Backdrop rects → `pointer-events: auto` (click ends the tour).
 *   • Gold spotlight ring → `pointer-events: none` (never blocks clicks).
 *   • Skip corner button → `pointer-events: auto`.
 *
 * Sentinel targets:
 *   • `null` (welcome) → single full-viewport backdrop, no cut-out.
 *   • `"roboball"`     → spotlight the FAB itself.
 */
export default function FiguTourOverlay() {
  const { currentStep, endTour } = useFiguTour();
  const [rect, setRect] = useState(null);
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== "undefined" ? window.innerWidth : 1280,
    h: typeof window !== "undefined" ? window.innerHeight : 800,
  }));

  useLayoutEffect(() => {
    if (!currentStep) {
      setRect(null);
      return undefined;
    }
    let raf = null;
    let cancelled = false;

    const resolveTargetEl = () => {
      if (currentStep.target === null) return null;
      if (currentStep.target === "roboball") {
        return document.querySelector('[data-testid="roboball-fab"]');
      }
      return document.querySelector(currentStep.target);
    };

    const compute = () => {
      const el = resolveTargetEl();
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({
        x: r.left - HALO_PADDING,
        y: r.top - HALO_PADDING,
        w: r.width + HALO_PADDING * 2,
        h: r.height + HALO_PADDING * 2,
      });
    };

    const loop = () => {
      if (cancelled) return;
      compute();
      raf = requestAnimationFrame(loop);
    };
    loop();

    const onResize = () => {
      setViewport({ w: window.innerWidth, h: window.innerHeight });
      compute();
    };
    window.addEventListener("resize", onResize);

    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => compute());
      ro.observe(document.body);
    }

    return () => {
      cancelled = true;
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, [currentStep]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") endTour();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [endTour]);

  if (!currentStep) return null;

  const { w: vw, h: vh } = viewport;
  const isWelcome = currentStep.target === null;

  const rectBase = {
    position: "fixed",
    background: BACKDROP,
    backdropFilter: BLUR_CSS,
    WebkitBackdropFilter: BLUR_CSS,
    pointerEvents: "auto",
    cursor: "default",
  };

  const onBackdropClick = () => endTour();

  const node = (
    <div
      className="figutour-overlay"
      data-testid="figutour-overlay"
      data-figutour-step-id={currentStep.id}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9998,
        // Container itself passes clicks through — children explicitly own it.
        pointerEvents: "none",
      }}
    >
      {isWelcome || !rect ? (
        <div
          className="figutour-backdrop-full"
          data-testid="figutour-backdrop-full"
          onClick={onBackdropClick}
          style={{ ...rectBase, left: 0, top: 0, width: vw, height: vh }}
        />
      ) : (
        <>
          {/* TOP */}
          <div
            className="figutour-backdrop-top"
            data-testid="figutour-backdrop-top"
            onClick={onBackdropClick}
            style={{
              ...rectBase,
              left: 0,
              top: 0,
              width: vw,
              height: Math.max(0, rect.y),
            }}
          />
          {/* LEFT */}
          <div
            className="figutour-backdrop-left"
            data-testid="figutour-backdrop-left"
            onClick={onBackdropClick}
            style={{
              ...rectBase,
              left: 0,
              top: rect.y,
              width: Math.max(0, rect.x),
              height: rect.h,
            }}
          />
          {/* RIGHT */}
          <div
            className="figutour-backdrop-right"
            data-testid="figutour-backdrop-right"
            onClick={onBackdropClick}
            style={{
              ...rectBase,
              left: rect.x + rect.w,
              top: rect.y,
              width: Math.max(0, vw - (rect.x + rect.w)),
              height: rect.h,
            }}
          />
          {/* BOTTOM */}
          <div
            className="figutour-backdrop-bottom"
            data-testid="figutour-backdrop-bottom"
            onClick={onBackdropClick}
            style={{
              ...rectBase,
              left: 0,
              top: rect.y + rect.h,
              width: vw,
              height: Math.max(0, vh - (rect.y + rect.h)),
            }}
          />
          {/* Gold ring — non-interactive. MUST stay pointer-events:none. */}
          <div
            className="figutour-spotlight"
            data-testid="figutour-spotlight"
            style={{
              position: "fixed",
              left: rect.x,
              top: rect.y,
              width: rect.w,
              height: rect.h,
              border: "2px solid #E5B83A",
              borderRadius: 10,
              boxShadow:
                "0 0 24px rgba(229, 184, 58, 0.5), inset 0 0 10px rgba(229, 184, 58, 0.15)",
              pointerEvents: "none",
            }}
          />
        </>
      )}

      {/* Skip corner — always available */}
      <button
        type="button"
        data-testid="figutour-skip-corner"
        onClick={() => endTour()}
        style={{
          position: "fixed",
          top: 16,
          right: 16,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.7)",
          fontSize: 12,
          cursor: "pointer",
          pointerEvents: "auto",
          padding: "6px 10px",
          borderRadius: 6,
        }}
      >
        Skip tour ✕
      </button>
    </div>
  );

  return createPortal(node, document.body);
}
